import "./calendar-scheduler.scss";

import { useUserIdentityContext } from "@core/services/user-identity.context";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import FormRegistration from "@domain/registration/form-registration/form-registration";
import {
  RegistrationFormRule,
  RegistrationFormSchema,
} from "@factory/form/form.rule";
import { GetRegistrationModel } from "@factory/model/query-model-registration";
import {
  useQyDeleteRegistration,
  useQyUpdateRegistration,
} from "@factory/query/registration.query";
import { useQyGetTenantById } from "@factory/query/tenant.query";
import { useQyGetUtilService } from "@factory/query/util-service.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { EventClickArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { zodResolver } from "@hookform/resolvers/zod";
import { LabelValue } from "@shared/models/label-value.interface";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { calendarUtils } from "@shared/utility/calendar-utils/calendar-utils";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { useState } from "react";
import { FieldErrors, FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useCalendarEvents } from "./calendar-events.hook";
import BookingDialog from "./components/booking-dialog/booking-dialog";
import RegistrationActions from "./components/registration-actions/registration-actions";
import RegistrationSummary from "./components/registration-summary/registration-summary";
import StatusLegend from "./components/status-legend/status-legend";
import { useRegistrationActions } from "./registration-actions.hook";

/* eslint-disable-next-line */
export interface CalendarSchedulerProps {
  registrationData: GetRegistrationModel[];
  serviceOptions?: Array<{ label: string; value: string }>;
  isFetchingServices?: boolean;
  defaultDate?: string;
}

export function CalendarScheduler({
  registrationData,
  serviceOptions = [],
  isFetchingServices = false,
  defaultDate,
}: CalendarSchedulerProps) {
  // STATE
  const [selectedRegistration, setSelectedRegistration] =
    useState<GetRegistrationModel | null>(null);
  const [showFullForm, setShowFullForm] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const navigate = useNavigate();

  // HOOKS
  const { formatDateForInput } = useDateTimeContext();
  const { getUserRole } = useUserIdentityContext();
  const { tenant } = getUserRole();

  // Fetch tenant data to check show_services_in_public_booking
  const { data: tenantResponse } = useQyGetTenantById(tenant || "");
  const tenantData = tenantResponse?.data?.[0];
  const showServicesInPublicBooking = Boolean(
    tenantData?.show_services_in_public_booking,
  );

  // Get services from util service
  const { data: utilServiceResponse, isLoading: isFetchingUtilServices } =
    useQyGetUtilService(
      "",
      999,
      0,
      undefined,
      undefined,
      showServicesInPublicBooking,
    );
  const utilServiceData = utilServiceResponse?.data || [];
  const utilServiceOptions = utilServiceData.map(
    (item) =>
      ({
        label: item.name,
        value: item.code,
      }) as LabelValue,
  );
  const { mapUpdateRegistration, mapDeleteRegistration } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { handleApproveDecline, isCreating } = useRegistrationActions();
  const { calendarEvents, unassignedEvents } =
    useCalendarEvents(registrationData);

  // FORM SETUP
  const formMethod = useForm<RegistrationFormSchema>({
    resolver: zodResolver(RegistrationFormRule),
  });
  const { handleSubmit, setValue, reset } = formMethod;

  // API UPDATE
  const handleUpdateApiSuccess = () => {
    showSuccess("Registration updated successfully");
    setShowFullForm(false);
  };
  const { mutate: updateRegistration, isLoading: isUpdating } =
    useQyUpdateRegistration(handleUpdateApiSuccess);

  // API DELETE Registration
  const handleDeleteApiSuccess = () => {
    clearSelection();
    showSuccess("Registration deleted successfully");
  };
  const { mutate: deleteRegistration } = useQyDeleteRegistration(
    handleDeleteApiSuccess,
  );

  const handleBack = () => {
    navigate("../");
  };

  const handleDelete = () => {
    confirmDialog({
      message: "Are you sure you want to delete this record?",
      header: "Delete Confirmation",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => {
        if (!selectedRegistration?.code) {
          throw new Error("No registration data found");
        }
        const form = mapDeleteRegistration(selectedRegistration?.code || "");
        deleteRegistration(form);
      },
    });
  };

  // FORM HANDLERS
  const populateForm = (responseData: GetRegistrationModel) => {
    if (!responseData) return;

    setValue("tenant", responseData.tenant || "");
    setValue("firstName", responseData.first_name || "");
    setValue("middleName", responseData.middle_name || "");
    setValue("lastName", responseData.last_name || "");
    setValue("gender", responseData.gender || "");
    setValue("address", responseData.address || "");
    setValue("mobileNumber", responseData.mobile_number || "");
    setValue("avatar", responseData.avatar || "");
    setValue("email", responseData.email || "");
    setValue("medicationDetails", responseData.medication_details || "");
    setValue("allergyDetails", responseData.allergy_details || "");
    setValue(
      "medicalConditonDetails",
      responseData.medical_conditon_details || "",
    );
    setValue("unwellDetails", responseData.unwell_details || "");
    setValue(
      "otherMedicalConditions",
      responseData.other_medical_conditions || "",
    );
    setValue("service", responseData.service || "");
    setValue("serviceType", responseData.service_type || "");
    setValue("time", responseData.time || "");
    setValue("otherRemarks", responseData.other_remarks || "");
    setValue("status", responseData.status || "");

    setValue("isTakingMedication", Boolean(responseData.is_taking_medication));
    setValue("hasAllergies", Boolean(responseData.has_allergies));
    setValue(
      "hasMedicalConditions",
      Boolean(responseData.has_medical_conditions),
    );
    setValue("wasRecentlyUnwell", Boolean(responseData.was_recently_unwell));

    if (responseData.birthday) {
      setValue("birthday", formatDateForInput(responseData.birthday));
    }
    if (responseData.date) {
      setValue("date", formatDateForInput(responseData.date));
    }
  };
  const handleValidate = (form: RegistrationFormSchema) => {
    if (!selectedRegistration?.code) {
      throw new Error("No registration data");
    }

    const updatedForm = {
      ...form,
      tenant: selectedRegistration.tenant,
    };

    const formData = mapUpdateRegistration(
      updatedForm,
      selectedRegistration.code,
    );
    updateRegistration(formData);
  };

  const handleValidateError = (err: FieldErrors<RegistrationFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  // EVENT HANDLERS
  const handleEventClick = (info: EventClickArg) => {
    const eventData = info.event.extendedProps as GetRegistrationModel;
    setSelectedRegistration(eventData);
    setShowFullForm(false);
    populateForm(eventData);
  };

  const handleDateClick = (info: DateClickArg) => {
    // Open booking dialog when clicking on a date
    setSelectedDate(info.dateStr);
    setShowBookingDialog(true);
  };

  const handleBookingCreated = () => {
    // Refresh the calendar data after booking is created
    setShowBookingDialog(false);
    setSelectedDate("");
    // The parent component will refetch registrations via react-query invalidation
  };

  const handleUnscheduledClick = (registration: GetRegistrationModel) => {
    setSelectedRegistration(registration);
    setShowFullForm(false);
    populateForm(registration);
  };

  const clearSelection = () => {
    setSelectedRegistration(null);
    setShowFullForm(false);
    reset();
  };

  const handleApprove = () => {
    if (selectedRegistration) {
      handleApproveDecline(
        selectedRegistration,
        "APPROVE",
        setSelectedRegistration,
      );
    }
  };

  const handleDecline = () => {
    if (selectedRegistration) {
      handleApproveDecline(
        selectedRegistration,
        "DECLINE",
        setSelectedRegistration,
      );
    }
  };

  // RENDER FUNCTIONS
  const renderEventContent = (eventInfo: {
    event: {
      start: Date;
      end?: Date;
      title: string;
      allDay?: boolean;
      extendedProps: Record<string, any>;
    };
  }) => {
    const { start, end, title, allDay, extendedProps } = eventInfo.event;
    const registration = extendedProps as GetRegistrationModel;
    const status = registration.status || "PENDING";
    const bgColor = calendarUtils.getStatusColor(status);

    // Only show time if the event has a time (not an all-day event)
    const hasTime = !allDay && registration.time;

    const formattedStart = hasTime
      ? new Date(start).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "";

    const formattedEnd =
      hasTime && end
        ? new Date(end).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
        : "";

    return (
      <div
        className={`${bgColor} text-white p-2 rounded-md shadow-md text-xs w-full`}
      >
        <p className="font-bold truncate">{title}</p>
        {hasTime && (
          <p className="truncate">
            {formattedStart} - {formattedEnd}
          </p>
        )}
        <p className="truncate">
          {registration?.service_name || "Assigned Service"}
        </p>
      </div>
    );
  };

  const renderRightPanel = () => {
    if (selectedRegistration) {
      return (
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-lg text-slate-blue-custom">
              {showFullForm ? "Edit Registration" : "Registration Details"}
            </h3>
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-sm"
              onClick={clearSelection}
            />
          </div>

          {!showFullForm ? (
            <>
              <RegistrationSummary registration={selectedRegistration} />
              <RegistrationActions
                registration={selectedRegistration}
                onUpdateClick={() => setShowFullForm(true)}
                onApproveClick={handleApprove}
                onDeclineClick={handleDecline}
                onDeleteClick={handleDelete}
                isLoading={isCreating}
              />
            </>
          ) : (
            <>
              <div className="overflow-y-auto max-h-[600px]">
                <FormProvider {...formMethod}>
                  <FormRegistration
                    serviceOptions={
                      showServicesInPublicBooking
                        ? utilServiceOptions
                        : serviceOptions
                    }
                    isFetchingServices={
                      showServicesInPublicBooking
                        ? isFetchingUtilServices
                        : isFetchingServices
                    }
                    isEdit={true}
                    showServicesInPublicBooking={showServicesInPublicBooking}
                  />
                </FormProvider>
              </div>
              <div className="mb-4 flex gap-2 pt-4">
                <Button
                  label="Save Changes"
                  icon="pi pi-save"
                  loading={isUpdating}
                  onClick={handleSubmit(handleValidate, handleValidateError)}
                  className="flex-1 p-button-sm"
                />
                <Button
                  label="Cancel"
                  icon="pi pi-arrow-left"
                  onClick={() => setShowFullForm(false)}
                  className="p-button-sm"
                  severity="secondary"
                  outlined
                />
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <div className="p-4">
        <h3 className="font-semibold text-lg text-slate-blue-custom mb-4">
          Unscheduled Registrations
        </h3>

        <div className="overflow-y-auto max-h-[600px]">
          {unassignedEvents.length > 0 ? (
            unassignedEvents.map((registration) => (
              <div
                key={registration.code}
                className="mb-2 p-3 bg-navy-blue-custom text-white rounded cursor-pointer hover:bg-slate-blue-custom transition-colors"
                onClick={() => handleUnscheduledClick(registration)}
              >
                <div className="font-medium text-sm">
                  {calendarUtils.formatEventTitle(registration)}
                </div>
                <div className="text-xs mt-1 opacity-80">
                  Click to view details
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <p className="text-gray-500">No unscheduled registrations</p>
            </div>
          )}
        </div>
        <StatusLegend />
      </div>
    );
  };

  return (
    <div id="CalendarScheduler" className="calendar-scheduler">
      <div className="calendar-container flex gap-4">
        {/* FULL CALENDAR COMPONENT */}
        <div className="text-sm my-4 w-3/4 font-poppins">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={defaultDate ? new Date(defaultDate) : undefined}
            editable={true}
            droppable={true}
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            events={calendarEvents}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            height="auto"
            contentHeight="auto"
            dayMaxEventRows={true}
            views={{
              dayGridMonth: {
                dayMaxEventRows: 2,
              },
            }}
            eventContent={renderEventContent}
          />
        </div>

        {/* RIGHT PANEL */}
        <div className="w-1/4 my-4 border border-gray-300 rounded-lg bg-white">
          {renderRightPanel()}
        </div>
      </div>

      {/* BOOKING DIALOG */}
      <BookingDialog
        visible={showBookingDialog}
        selectedDate={selectedDate}
        onHide={() => setShowBookingDialog(false)}
        onBookingCreated={handleBookingCreated}
      />
    </div>
  );
}

export default CalendarScheduler;
